// =====================================================================
// src/lib/proprietarios.js — o cadastro do dono, que é da imobiliária (0101)
//
// Hugo, 02/11/2026: "o proprietário não tem acesso a nenhuma informação,
// nem tem cadastro no sistema, salvo ele seja considerado como uma
// imobiliária". O cadastro do dono (nome, CPF/CNPJ, contato, Pix) passa a
// ser mantido pela imobiliária que administra os flats dele — o hotel
// deixa de ver. As telas são a aba "Flats" (`modulos/flats.js`, cópia de
// `renderFlats`, MdM 10485), o modal "Proprietários" (`modulos/
// proprietarios.js`, cópia de `modalGerenciarProprietarios` 10128,
// `modalNovoProprietario` 10171 e `editarProprietario` 10265) e o seletor
// "Proprietário" no detalhe do quarto do Dashboard.
//
// Funções PURAS: nada de DOM, nada de rede — testadas em
// scripts/teste_tela.mjs. Quem autoriza é o banco (0101):
//   proprietarios_da_imobiliaria()  ver flats    — os donos DELA
//   salvar_proprietario(...)        editar flats — nome obrigatório e único
//   excluir_proprietario(p_id)      editar flats — 'excluido' | 'desativado'
//   reativar_proprietario(p_id)     editar flats — o desfazer do desativar
//   definir_proprietario_do_flat    editar flats — flat e dono DELA, dono ativo
// O que está aqui só decide o que desenhar e avisa antes, com as mesmas
// palavras do banco; se divergir, a tela mostra e o banco recusa.
//
// Divergências do MdM (decisão do Hugo, esboço da 0101):
//   · sem a foto do dono (lá é obrigatória) e com CPF/CNPJ (aqui o
//     cadastro já guardava o documento);
//   · excluir só o dono sem flat nem acordo de repasse — com um dos dois o
//     banco DESATIVA (o financeiro por dono fica, `acordos_da_imobiliaria`);
//   · "Quarto N" no lugar de "Flat N" (plano da 0101).
// =====================================================================
import { documentoValido } from './termo.js';

export const SEM_PROPRIETARIO = 'Sem proprietário';

// O nível do módulo `flats` (nível imobiliária, 0101) — só para quem entra
// como imobiliária. O Administrador do Sistema não tem a quem perguntar:
// `proprietarios_da_imobiliaria()` é da imobiliária logada.
export function nivelDeFlats(contexto) {
  const c = contexto || {};
  if (!c.imobiliaria || !c.imobiliaria.id) return null;
  const m = (Array.isArray(c.modulos) ? c.modulos : []).find(x => x && x.chave === 'flats');
  return m && (m.nivel === 'ver' || m.nivel === 'editar') ? m.nivel : null;
}
export const podeVerProprietarios = c => nivelDeFlats(c) !== null;
export const podeEditarProprietarios = c => nivelDeFlats(c) === 'editar';

// ── O FORMULÁRIO (Novo / Editar) ─────────────────────────────────────
// As mensagens do documento são as do banco (as mesmas do aceite do
// Termo, 0073/0074): a tela só avisa antes de mandar.
export function validarProprietario({ nome, documento } = {}) {
  const erros = [];
  if (!String(nome || '').trim()) erros.push('Informe o nome do proprietário');
  const doc = String(documento || '').trim();
  if (doc) {
    if (/[^\d.\-/\s]/.test(doc)) erros.push('CPF ou CNPJ tem só números (pontos, barra e traço podem ir)');
    else if (!documentoValido(doc)) erros.push('CPF ou CNPJ inválido — confira os dígitos');
  }
  return erros;
}

// O que vai para `salvar_proprietario`: campo vazio vira null (o banco
// guarda nulo, e null na rede diz o que se quis dizer).
export function argumentosDoProprietario(id, f = {}) {
  const ou = x => (x == null || String(x).trim() === '' ? null : String(x).trim());
  return { p_id: id || null, p_nome: String(f.nome || '').trim(), p_documento: ou(f.documento),
           p_telefone: ou(f.telefone), p_email: ou(f.email), p_pix: ou(f.pix) };
}

// ── A LINHA DO MODAL ─────────────────────────────────────────────────
// `modalGerenciarProprietarios` (10128): telefone e e-mail em cinza, o Pix
// em índigo com <code>, ou "sem dados de contato/pix" em itálico.
export function contatoDoProprietario(p) {
  const x = p || {};
  const t = v => (v && String(v).trim() ? String(v).trim() : null);
  const telefone = t(x.telefone), email = t(x.email), pix = t(x.pix);
  return { telefone, email, pix, vazio: !telefone && !email && !pix };
}

// Ativos primeiro, cada grupo por nome (o banco já manda por nome; isto só
// põe os desativados no fim, para a lista não abrir com quem saiu).
export function ordenarProprietarios(lista) {
  return (lista || []).slice().sort((a, b) =>
    ((a.ativo === false) - (b.ativo === false)) || String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR'));
}

// O que dizer depois de `excluir_proprietario`.
export function avisoDaExclusao(resultado, nome) {
  if (resultado === 'desativado') return `${nome} tem flat ou acordo de repasse: o cadastro foi desativado, não excluído.`;
  return `${nome} excluído.`;
}

// E depois de `reativar_proprietario` (Hugo, 02/11: "Reativar" no lugar da
// lixeira do desativado).
export const avisoDaReativacao = nome => `${nome} reativado: volta a aparecer no seletor dos flats.`;

// ── A ABA FLATS (`renderFlats`, 10485) ───────────────────────────────
// Os flats DELA agrupados por proprietário, cada grupo por nome do dono e
// o "Sem proprietário" no fim. O nome vem do cadastro dela
// (`proprietarios_da_imobiliaria`), não de outra leitura.
const porNumero = (a, b) => String(a.numero).localeCompare(String(b.numero), undefined, { numeric: true });
export function gruposDosFlats(unidades, proprietarios) {
  const nomes = new Map((proprietarios || []).map(p => [p.id, p.nome]));
  const grupos = new Map();
  for (const u of (unidades || []).filter(x => x && x.ativo !== false)) {
    const id = u.proprietario_id || null;
    const titulo = id ? (nomes.get(id) || 'Proprietário fora do cadastro') : SEM_PROPRIETARIO;
    const chave = id || '';
    if (!grupos.has(chave)) grupos.set(chave, { id, titulo, quartos: [] });
    grupos.get(chave).quartos.push(u);
  }
  return [...grupos.values()]
    .map(g => ({ ...g, quartos: g.quartos.slice().sort(porNumero) }))
    .sort((a, b) => (!a.id) - (!b.id) || a.titulo.localeCompare(b.titulo, 'pt-BR'));
}

// ── O SELETOR "Proprietário" (card da aba Flats e detalhe do Dashboard) ─
// Os ATIVOS por nome, e "— sem proprietário —" no fim (o esboço). O dono
// atual desativado continua aparecendo, selecionado e sem poder ser
// escolhido de novo: `definir_proprietario_do_flat` só aceita dono ativo.
export function opcoesDoSeletor(proprietarios, atualId) {
  const lista = ordenarProprietarios(proprietarios);
  const opcoes = lista.filter(p => p.ativo !== false || p.id === atualId)
    .map(p => ({ valor: p.id, rotulo: p.ativo === false ? `${p.nome} (inativo)` : p.nome, desabilitado: p.ativo === false }));
  opcoes.push({ valor: '', rotulo: '— sem proprietário —', desabilitado: false });
  return opcoes;
}

// O que vai para `definir_proprietario_do_flat`: '' (sem proprietário)
// vira nulo.
export const valorDoSeletor = v => (v === '' || v == null ? null : String(v));

// A frase depois de trocar.
export function avisoDaTroca(numero, nome) {
  return nome ? `Quarto ${numero}: proprietário ${nome}.` : `Quarto ${numero} ficou sem proprietário.`;
}
